import { getAuthUser } from '../../../spa/store/general/selectors';
import { getState } from '../../../spa/store';
import { SyncStorage } from './local-storage-helpers';
import { EPhrasesColumnTab } from '../../../spa/store/general/types';

export enum EUserStateStoragePageType {
  VIDEO = 1, AUDIO = 2, CONTENT_LIB = 3, MY_STUFF = 4, TRANSCRIPT = 5
}
export type TUserStateStorage = {
  libraryGroupId?: number;
  groupId?: number;
  videoId?: string;
  phraseId?: number;
  audioId?: number | null;
  pageType?: EUserStateStoragePageType;
  savedCount?: number;
  phrasesColumnTab?: EPhrasesColumnTab;
  backFromText?: boolean;
  backUrl?: string;
  speechLangVoiceMap?: Record<string, string>
}

export class AnonymUserStorage {

  private static KEY = 'anonymUserStorage';

  public static setHash(hash: string) {
    window.localStorage.setItem(this.KEY, hash);
  }

  public static getHash(): string {
    return window.localStorage.getItem(this.KEY) || '';
  }

  public static remove() {
    window.localStorage.removeItem(this.KEY);
  }
}

export class UserStateStorage extends SyncStorage {

  default: TUserStateStorage;

  constructor(fold: TUserStateStorage) {
    super();
    this.default = fold;
  }

  private getStorageKey() {
    const user = getAuthUser(getState());
    return 'user-state' + (user ? '-' + user.hashId : '');
  }

  getData(): TUserStateStorage {
    return this.getItem(this.getStorageKey(), this.default);
  }
  saveData(value: TUserStateStorage): TUserStateStorage {
    this.saveItem(this.getStorageKey(), value);
    return value;
  }

  public setLibraryGroupId(libraryGroupId: number) {
    const data: TUserStateStorage = {
      ...this.getData(),
      ...{libraryGroupId}
    }
    this.saveData(data);
  }

  public setAudioInfo(audioId: number | null, groupId: number | undefined) {
    const data: TUserStateStorage = {
      ...this.getData(),
      ...{audioId, videoId: undefined, groupId, phraseId: undefined, videoPosition: undefined, pageType: EUserStateStoragePageType.AUDIO}
    }
    this.saveData(data);
  }

  public setMyStuffPage() {
    this.saveData({
      ...this.getData(),
      pageType: EUserStateStoragePageType.MY_STUFF
    });
  }

  public setContentLibPage() {
    this.saveData({
      ...this.getData(),
      pageType: EUserStateStoragePageType.CONTENT_LIB
    });
  }

  public setTranscriptPage() {
    this.saveData({
      ...this.getData(),
      pageType: EUserStateStoragePageType.TRANSCRIPT
    });
  }

  public incSavedCount() {
    let data = this.getData() || {};
    let { savedCount = 0 } = data;
    this.saveData({
      ...this.getData(),
      savedCount: savedCount + 1,
    });
  }

  public setSpeechLangVoiceMap(speechLangVoiceMap: Record<string, string>) {
    this.saveData({
      ...this.getData(),
      speechLangVoiceMap,
    });
  }

  public setBackFromText (backFromText: boolean) {
    let data = {...this.getData()};
    if (!backFromText) {
      delete data.backFromText;
    } else {
      data.backFromText = backFromText;
    }
    this.saveData(data);
  }

  public setBackUrl(backUrl: string) {
    this.saveData({
      ...this.getData(),
      backUrl,
    });
  }

  public setPhrasesColumnTab (phrasesColumnTab: EPhrasesColumnTab) {
    this.saveData({
      ...this.getData(),
      phrasesColumnTab,
    });
  }

  // clear all user params
  public clearAll() {
    this.saveData({});
  }

  // clear only user runtime params except speechLangVoiceMap
  public clearRuntimeParams() {
    const speechLangVoiceMap = this.getData().speechLangVoiceMap;
    this.saveData({speechLangVoiceMap});
  }
}

const userStateStorage = new UserStateStorage({});
export const getUserStateStorage = (): UserStateStorage => {
  return userStateStorage;
}
